/**
 * Zobrazení chyby partial updatu uživateli (issue #239).
 *
 * Partial update polyká selhání: když server vrátí 500, timeout nebo cokoli jiného než očekávaný fragment,
 * `throwPUError` jen vyhodí JS výjimku a v DOM se nezmění nic — uživatel klikne na „Odeslat" a nestane se
 * vůbec nic. U kariérního formuláře to znamená ztracenou přihlášku, o které uchazeč neví.
 *
 * `fg-frontend` pro ten případ volá `settings.onError` (nebo globální `onError` z nastavení partial updatu),
 * viz `fg-frontend/src/partialUpdate/partialUpdate.js` → `throwPUError`. Tenhle modul takový handler
 * poskytuje: vyrenderuje hlášku do kontejneru, který se měl aktualizovat, a chybu zaloguje do konzole
 * s HTTP statusem a URL pro případnou diagnostiku.
 */

const NOTICE_CLASS = 'js-partial-update-error';
const DEFAULT_TEXT = 'Požadavek se nepodařilo zpracovat, data nebyla odeslána. '
  + 'Zkuste to prosím znovu.';

/**
 * Najde element, do kterého se hláška vloží. Preferuje kontejner, který se měl aktualizovat, aby hláška
 * vyskočila tam, kam se uživatel dívá (typicky uvnitř modálního okna formuláře).
 *
 * @param {Object} settings nastavení partial updatu z fg-frontend
 * @returns {?Element} element pro vložení hlášky
 */
const resolveContainer = (settings = {}) => {
  const { targetElement, target } = settings;

  if (targetElement && targetElement.nodeType === 1) {
    return targetElement;
  }

  const targetIds = Array.isArray(target) ? target : [target];
  const byId = targetIds
    .filter(id => typeof id === 'string')
    .map(id => document.getElementById(id))
    .find(element => element != null);
  if (byId) {
    return byId;
  }

  const visibleContainer = Array.from(document.querySelectorAll('.isPartialUpdateContainer'))
    .find(element => element.getClientRects().length > 0);

  return visibleContainer || document.querySelector('main') || document.body;
};

/**
 * Vloží (nebo nahradí) hlášku o neúspěšném partial updatu.
 *
 * @param {Object} params parametry z `throwPUError`
 */
export const showPartialUpdateError = ({ message, jqXHR, settings } = {}) => {
  const status = jqXHR && typeof jqXHR.status === 'number' ? jqXHR.status : null;
  const url = settings && settings.url ? settings.url : null;

  // diagnostika do konzole zůstává vždy, i kdyby se hlášku nepodařilo vykreslit
  // eslint-disable-next-line no-console
  console.error('[partialUpdate] %s (HTTP %s, %s)', message || 'chyba', status, url);

  try {
    const container = resolveContainer(settings);
    if (!container) {
      return;
    }

    const previous = container.querySelector(`.${NOTICE_CLASS}`);
    if (previous) {
      previous.remove();
    }

    const notice = document.createElement('div');
    notice.className = `alert alert--danger alert-danger ${NOTICE_CLASS}`;
    notice.setAttribute('role', 'alert');
    notice.setAttribute('tabindex', '-1');
    if (status != null) {
      notice.setAttribute('data-http-status', String(status));
    }
    if (url) {
      notice.setAttribute('data-request-url', url);
    }
    notice.textContent = DEFAULT_TEXT;

    container.insertBefore(notice, container.firstChild);
    notice.scrollIntoView({ block: 'nearest' });
    notice.focus({ preventScroll: true });
  } catch (e) {
    // hláška je doplňková - nikdy nesmí shodit zbytek zpracování chyby
    // eslint-disable-next-line no-console
    console.error('[partialUpdate] hlášku o chybě se nepodařilo zobrazit', e);
  }
};

export default showPartialUpdateError;



// WEBPACK FOOTER //
// ./fe/web-default/src/js/partialUpdateErrorNotice.js